import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ActiveUsersHistoryRange,
  useActiveUsersHistory,
} from '../api/useActiveUsersHistory';
import { formatTick } from '@/pages/B2B/user-insights/utils/formatTick';

type RangeOption = { key: ActiveUsersHistoryRange; label: string };

const RANGE_OPTIONS: RangeOption[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

function formatPeakTimestamp(tsSeconds: number): string {
  const d = new Date(tsSeconds * 1000);
  return d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface TooltipPayloadItem {
  value?: number;
  payload?: { ts?: number; total?: number; consumer?: number; business?: number };
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  range: ActiveUsersHistoryRange;
}

function ChartTooltip({ active, payload, range }: ChartTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p || typeof p.ts !== 'number') return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm">
      <div className="text-gray-400 mb-1">{formatTick(p.ts, range)}</div>
      <div className="text-white font-semibold">Total: {p.total ?? 0}</div>
      <div className="text-emerald-300">Consumer: {p.consumer ?? 0}</div>
      <div className="text-violet-300">Business: {p.business ?? 0}</div>
    </div>
  );
}

interface ActiveUsersChartProps {
  className?: string;
}

export function ActiveUsersChart({
  className,
}: ActiveUsersChartProps): React.ReactElement {
  const [range, setRange] = useState<ActiveUsersHistoryRange>('day');
  const { data, loading, error } = useActiveUsersHistory(range);

  const points = data?.points ?? [];
  const peak = data?.peak ?? null;

  // Pre-format ticks once per data refresh.
  const xTickFormatter = useMemo(
    () => (value: number) => formatTick(value, range),
    [range],
  );

  // Pick a sensible number of X-axis ticks based on point count.
  const tickCount = points.length === 0
    ? 0
    : Math.min(6, Math.max(2, Math.floor(points.length / 12)));

  return (
    <div
      className={`bg-gray-900/60 border border-gray-800 rounded-2xl p-5 ${
        className ?? ''
      }`}
    >
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-white font-semibold text-base">Active Users — History</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Sampled every minute. Peak in range is computed from the raw 1‑min samples.
          </p>
        </div>

        <div className="flex items-center gap-1 self-start sm:self-auto bg-gray-950/60 border border-gray-800 rounded-full p-1">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setRange(opt.key)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                range === opt.key
                  ? 'bg-violet-700 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-800'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Peak banner */}
      <div className="flex items-center justify-between bg-gray-950/40 border border-gray-800 rounded-xl px-4 py-3 mb-4">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-violet-700 to-pink-700 text-white">
            ▲
          </span>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-400">
              Peak this {range}
            </div>
            <div className="text-white font-display text-2xl leading-none">
              {peak ? peak.total.toLocaleString() : '—'}
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-400 text-right">
          {peak
            ? formatPeakTimestamp(peak.ts)
            : loading
            ? 'Loading…'
            : 'No samples yet'}
        </div>
      </div>

      {/* Chart */}
      <div className="h-64 w-full">
        {error && (
          <div className="h-full flex items-center justify-center text-sm text-red-400">
            {error}
          </div>
        )}
        {!error && points.length === 0 && (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            {loading ? 'Loading…' : 'No data in this range yet — give the sampler a few minutes.'}
          </div>
        )}
        {!error && points.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={points}
              margin={{ top: 8, right: 8, left: -12, bottom: 0 }}
            >
              <defs>
                <linearGradient id="totalFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
              <XAxis
                dataKey="ts"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={xTickFormatter}
                stroke="#6b7280"
                fontSize={11}
                tickCount={tickCount}
                minTickGap={32}
              />
              <YAxis
                allowDecimals={false}
                stroke="#6b7280"
                fontSize={11}
                width={40}
              />
              <Tooltip content={<ChartTooltip range={range} />} />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#a78bfa"
                strokeWidth={2}
                fill="url(#totalFill)"
                isAnimationActive={false}
              />
              {peak && (
                <ReferenceDot
                  x={peak.ts}
                  y={peak.total}
                  r={5}
                  fill="#ec4899"
                  stroke="#fff"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default ActiveUsersChart;
